import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DndContext, 
  DragEndEvent, 
  DragStartEvent, 
  useSensor, 
  useSensors, 
  PointerSensor,
  closestCenter,
  pointerWithin,
  DragOverlay,
  useDroppable
} from '@dnd-kit/core';
import { 
  SortableContext, 
  verticalListSortingStrategy, 
  useSortable 
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Search, 
  Filter, 
  Plus, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Upload, 
  Calendar, 
  Phone, 
  Mail, 
  MapPin, 
  Building, 
  User, 
  Star, 
  Clock, 
  Target, 
  TrendingUp, 
  AlertCircle,
  X,
  RefreshCw,
  Wallet,
  AlertTriangle,
  MessageSquare,
  CheckCircle,
  FileText,
  Download,
  Eye,
  Lock,
  Pause,
  Play
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lead } from '@/types/lead';
import { api } from '@/lib/api';
import { WonSubscriptionModal } from './subscription/WonSubscriptionModal';
import { StageBlockedModal } from './stage-gate/StageBlockedModal';
import { GateStatusIndicator } from './stage-gate/GateStatusIndicator';
import { GateCheckResult, BatchGateStatus, GateMissingItem } from '../types/stageGate';
import { useAuth } from '../contexts/AuthContext';

interface PipelineBoardProps {
  leads: Lead[];
  onLeadClick: (lead: Lead) => void;
  onDeleteLead: (id: string) => void;
  onLeadMove: (leadId: string, newStage: Lead['stage']) => void;
  onAddLead: () => void;
}

// Fixed Stage configuration
const STAGES = ['NEW', 'CONTACTED', 'DISCUSSION', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'] as const;

const getStageIcon = (stage: Lead['stage']) => {
  switch (stage) {
    case 'NEW': return '🌟';
    case 'CONTACTED': return '📞';
    case 'DISCUSSION': return '💬';
    case 'PROPOSAL': return '📋';
    case 'NEGOTIATION': return '🤝';
    case 'WON': return '🎉';
    case 'LOST': return '❌';
    default: return '📋';
  }
};

const getStageColor = (stage: Lead['stage']) => {
  switch (stage) {
    case 'NEW': return 'bg-blue-100 text-blue-800';
    case 'CONTACTED': return 'bg-yellow-100 text-yellow-800';
    case 'DISCUSSION': return 'bg-purple-100 text-purple-800';
    case 'PROPOSAL': return 'bg-orange-100 text-orange-800';
    case 'NEGOTIATION': return 'bg-indigo-100 text-indigo-800';
    case 'WON': return 'bg-green-100 text-green-800';
    case 'LOST': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

// FIXED: Droppable Stage Column with proper ID
const DroppableStageColumn: React.FC<{
  stage: Lead['stage'];
  leads: Lead[];
  stageStats?: { count: number; totalValue: number };
  onLeadClick: (lead: Lead) => void;
  onDeleteLead: (id: string) => void;
  onEditLead: (lead: Lead) => void;
  onHoldLead: (lead: Lead) => void;
  onResumeLead: (lead: Lead) => void;
  onAddLead: () => void;
  gateStatus?: BatchGateStatus;
}> = ({ stage, leads, stageStats, onLeadClick, onDeleteLead, onEditLead, onHoldLead, onResumeLead, onAddLead, gateStatus }) => {
  const { setNodeRef } = useDroppable({
    id: stage, // CRITICAL FIX: Use stage name as droppable ID
  });
  
  const stageLeads = leads.filter(lead => lead.stage === stage);
  const totalValue = stageStats?.totalValue || stageLeads.reduce((sum, lead) =>
    sum + (lead.dealValue || lead.value || 0), 0
  );

  return (
    <div 
      ref={setNodeRef} 
      className="flex flex-col h-full min-h-[600px] bg-gray-50 rounded-lg border-2 border-dashed border-transparent hover:border-blue-300 transition-colors"
      data-stage={stage}
    >
      {/* Stage Header */}
      <div className="bg-white rounded-t-lg p-4 border-b shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className="text-lg">{getStageIcon(stage)}</span>
            <h3 className="font-semibold text-gray-900">{stage}</h3>
          </div>
          <Badge variant="outline" className={getStageColor(stage)}>
            {stageStats?.count || stageLeads.length}
          </Badge>
        </div>
        
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-green-600">
            ₹{totalValue.toLocaleString()}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onAddLead}
            aria-label={`Add lead to ${stage} stage`}
            className="h-6 w-6 p-0 hover:bg-blue-100"
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Leads Container - EXPANDED DROPPABLE AREA */}
      <div className="flex-1 p-3 overflow-y-auto min-h-[400px]">
        <SortableContext items={stageLeads.map(lead => lead.id)} strategy={verticalListSortingStrategy}>
          <AnimatePresence>
            {stageLeads.map((lead) => (
              <SortableLeadCard
                key={lead.id}
                lead={lead}
                onClick={() => onLeadClick(lead)}
                onDelete={() => onDeleteLead(lead.id)}
                onEdit={() => onEditLead(lead)}
                onHold={() => onHoldLead(lead)}
                onResume={() => onResumeLead(lead)}
                isGateBlocked={gateStatus?.[lead.id]?.blocked}
                gateStatusData={gateStatus?.[lead.id]}
              />
            ))}
          </AnimatePresence>
        </SortableContext>
        
        {/* EXTENDED DROP ZONE */}
        <div className="min-h-[200px] w-full flex items-center justify-center">
          {stageLeads.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Building className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-xs font-medium opacity-50">No leads in {stage.toLowerCase()}</p>
              <p className="text-xs mt-1 opacity-30">Drag leads here to move</p>
            </div>
          ) : (
            <div className="text-center py-4 text-gray-300 text-xs opacity-30">
              Drop to add more leads
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Sortable Lead Card Component
const SortableLeadCard: React.FC<{
  lead: Lead;
  onClick: () => void;
  onDelete: () => void;
  onEdit: () => void;
  onHold: () => void;
  onResume: () => void;
  isGateBlocked?: boolean;
  gateStatusData?: { blocked: boolean; missingCount: number };
}> = ({ lead, onClick, onDelete, onEdit, onHold, onResume, isGateBlocked, gateStatusData }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lead.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const dealValue = lead.dealValue || lead.value || 0;

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`
        group relative mb-3 cursor-pointer
        ${isDragging ? 'scale-105 shadow-xl z-50' : 'hover:shadow-md'}
        transition-all duration-200
      `}
    >
      <Card
        className={`p-4 bg-white border hover:border-blue-300 transition-colors ${lead.isOnHold ? 'border-l-4 border-l-amber-400 bg-amber-50/30' : ''}`}
        onClick={(e) => {
          // Don't trigger click if dragging
          if (!isDragging) {
            e.stopPropagation();
            onClick();
          }
        }}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-gray-900 truncate">
                {lead.name}
              </h4>
              <GateStatusIndicator gateStatus={gateStatusData} />
              {lead.isOnHold && (
                <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px] px-2.5 py-1 whitespace-nowrap">
                  <span className="relative flex h-2 w-2 mr-1.5 inline-flex">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-600"></span>
                  </span>
                  On Hold{lead.holdUntil ? ` till ${new Date(lead.holdUntil).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''}
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-600 truncate">
              {lead.company}
            </p>
          </div>
          
          <div className="flex items-center gap-1">
            {/* Assigned-to initials avatar */}
            {((lead as any).assignedToUser?.name || (lead as any).assignedTo?.name) && (
              <div
                className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-medium shrink-0"
                title={(lead as any).assignedToUser?.name || (lead as any).assignedTo?.name}
              >
                {((lead as any).assignedToUser?.name || (lead as any).assignedTo?.name)?.charAt(0)?.toUpperCase()}
              </div>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  aria-label={`Actions for ${lead.name}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit()}>
                  <Edit className="h-3 w-3 mr-2" />
                  Edit Lead
                </DropdownMenuItem>
                {lead.isOnHold ? (
                  <DropdownMenuItem onClick={() => onResume()} className="text-green-600 focus:text-green-700">
                    <Play className="h-3 w-3 mr-2" />
                    Resume from Hold
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => onHold()} className="text-amber-600 focus:text-amber-700">
                    <Pause className="h-3 w-3 mr-2" />
                    Place on Hold
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => onDelete()} className="text-red-600 focus:text-red-700">
                  <Trash2 className="h-3 w-3 mr-2" />
                  Delete Lead
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-medium text-green-600">
              ₹{dealValue.toLocaleString()}
            </span>
            <Badge className={
              lead.priority === 'HOT' ? 'bg-red-100 text-red-700' :
              lead.priority === 'WARM' ? 'bg-orange-100 text-orange-700' :
              'bg-blue-100 text-blue-700'
            }>
              {lead.priority}
            </Badge>
          </div>

          {/* Payment Status Warning for WON leads */}
          {lead.stage === 'WON' && (
            <div className="flex items-center justify-between mt-1">
              {lead.paymentStatus === 'NO_PAYMENT' || !lead.paymentStatus ? (
                <Badge className="bg-red-100 text-red-700 border border-red-300 animate-pulse">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  No Payment Logged
                </Badge>
              ) : lead.paymentStatus === 'PARTIAL' ? (
                <Badge className="bg-orange-100 text-orange-700 border border-orange-300">
                  <Wallet className="h-3 w-3 mr-1" />
                  Partial: ₹{(lead.totalPaid || 0).toLocaleString()}
                </Badge>
              ) : (
                <Badge className="bg-green-100 text-green-700 border border-green-300">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Fully Paid
                </Badge>
              )}
            </div>
          )}
          
          {lead.contactPerson && (
            <div className="flex items-center space-x-1">
              <User className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-600 truncate">{lead.contactPerson}</span>
            </div>
          )}
          
          {lead.phoneNumber && (
            <div className="flex items-center space-x-1">
              <Phone className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-600">{lead.phoneNumber}</span>
            </div>
          )}
          
          {lead.expectedClose && (
            <div className="flex items-center space-x-1">
              <Calendar className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-500">
                {new Date(lead.expectedClose).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

// Lead Details Modal Component with ALL functionality fixed
const LeadDetailsModal: React.FC<{
  lead: Lead | null;
  isOpen: boolean;
  onClose: () => void;
}> = ({ lead, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [newActivity, setNewActivity] = useState({ type: 'CALL', description: '' });
  const [newNote, setNewNote] = useState('');
  const [newTask, setNewTask] = useState({ 
    title: '', description: '', dueDate: '', priority: 'MEDIUM' 
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch complete lead details with enhanced error handling
  const { data: leadDetails, isLoading: leadLoading, error: leadError } = useQuery({
    queryKey: ['lead-details', lead?.id],
    queryFn: async () => {
      if (!lead) return null;
      console.log('🔍 Fetching lead details for:', lead.id);
      try {
        const details = await api.getLead(lead.id);
        console.log('✅ Lead details fetched:', details);
        return details;
      } catch (error) {
        console.error('❌ Failed to fetch lead details:', error);
        throw error;
      }
    },
    enabled: !!lead && isOpen,
    retry: 2
  });

  // Fetch all related data
  const { data: activities = [], isLoading: activitiesLoading, refetch: refetchActivities } = useQuery({
    queryKey: ['lead-activities', lead?.id],
    queryFn: () => lead ? api.getLeadActivities(lead.id) : Promise.resolve([]),
    enabled: !!lead && isOpen
  });

  const { data: notes = [], isLoading: notesLoading, refetch: refetchNotes } = useQuery({
    queryKey: ['lead-notes', lead?.id],
    queryFn: () => lead ? api.getLeadNotes(lead.id) : Promise.resolve([]),
    enabled: !!lead && isOpen
  });

  const { data: tasks = [], isLoading: tasksLoading, refetch: refetchTasks } = useQuery({
    queryKey: ['lead-tasks', lead?.id],
    queryFn: () => lead ? api.getLeadTasks(lead.id) : Promise.resolve([]),
    enabled: !!lead && isOpen
  });

  const { data: files = [], isLoading: filesLoading, refetch: refetchFiles } = useQuery({
    queryKey: ['lead-files', lead?.id],
    queryFn: () => lead ? api.getLeadFiles(lead.id) : Promise.resolve([]),
    enabled: !!lead && isOpen
  });

  const { toast } = useToast();

  // FIXED: All mutations with proper error handling
  const addActivityMutation = useMutation({
    mutationFn: (data: { type: string; content: string }) => {
      console.log('🔄 Adding activity:', data);
      if (!lead?.id) throw new Error('No lead selected');
      return api.addActivity(lead.id, { 
        type: data.type, 
        content: data.content, 
        createdBy: 'current-user' 
      });
    },
    onSuccess: () => {
      refetchActivities();
      setNewActivity({ type: 'CALL', description: '' });
      toast({ title: '✅ Activity added successfully' });
    },
    onError: (error: any) => {
      console.error('❌ Failed to add activity:', error);
      toast({ 
        title: '❌ Failed to add activity', 
        description: error.message || 'Please try again',
        variant: 'destructive' 
      });
    }
  });

  const addNoteMutation = useMutation({
    mutationFn: (content: string) => {
      console.log('🔄 Adding note:', content.substring(0, 50) + '...');
      if (!lead?.id) throw new Error('No lead selected');
      return api.addNote(lead.id, content);
    },
    onSuccess: () => {
      refetchNotes();
      setNewNote('');
      toast({ title: '✅ Note added successfully' });
    },
    onError: (error: any) => {
      console.error('❌ Failed to add note:', error);
      toast({ 
        title: '❌ Failed to add note', 
        description: error.message || 'Please try again',
        variant: 'destructive' 
      });
    }
  });

  const addTaskMutation = useMutation({
    mutationFn: (data: any) => {
      console.log('🔄 Creating task:', data);
      if (!lead?.id) throw new Error('No lead selected');
      return api.createTask(lead.id, data);
    },
    onSuccess: () => {
      refetchTasks();
      setNewTask({ title: '', description: '', dueDate: '', priority: 'MEDIUM' });
      toast({ title: '✅ Task created successfully' });
    },
    onError: (error: any) => {
      console.error('❌ Failed to create task:', error);
      toast({ 
        title: '❌ Failed to create task', 
        description: error.message || 'Please try again',
        variant: 'destructive' 
      });
    }
  });

  const uploadFileMutation = useMutation({
    mutationFn: async (file: File) => {
      console.log('🔄 Uploading file:', file.name);
      if (!lead?.id) throw new Error('No lead selected');
      return api.uploadLeadFile(lead.id, file);
    },
    onSuccess: () => {
      refetchFiles();
      toast({ title: '✅ File uploaded successfully' });
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (error: any) => {
      console.error('❌ Failed to upload file:', error);
      toast({ 
        title: '❌ Failed to upload file', 
        description: error.message || 'Please try again',
        variant: 'destructive' 
      });
    }
  });

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) uploadFileMutation.mutate(file);
  };

  if (!lead || !isOpen) return null;

  const currentLead = leadDetails || lead;
  
  console.log('🔍 LeadDetailsModal Debug:', {
    originalLead: lead,
    leadDetails,
    currentLead,
    leadLoading,
    leadError,
    isModalOpen: isOpen
  });

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-2xl font-bold">{currentLead.name}</h2>
                <p className="text-blue-100">{currentLead.company}</p>
                <div className="flex items-center space-x-4 mt-2">
                  <Badge className={getStageColor(currentLead.stage)}>
                    {getStageIcon(currentLead.stage)} {currentLead.stage}
                  </Badge>
                  <span className="text-blue-100">
                    ₹{(currentLead.dealValue || currentLead.value || 0).toLocaleString()}
                  </span>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="info">Info</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
                <TabsTrigger value="tasks">Tasks</TabsTrigger>
                <TabsTrigger value="notes">Notes</TabsTrigger>
                <TabsTrigger value="files">Files</TabsTrigger>
              </TabsList>

              {/* Info Tab */}
              <TabsContent value="info" className="mt-6">
                {leadLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                    <span className="ml-2">Loading lead details...</span>
                  </div>
                ) : leadError ? (
                  <div className="text-center py-8 text-red-600">
                    <AlertCircle className="h-12 w-12 mx-auto mb-2" />
                    <p>Failed to load lead details</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Contact Person</label>
                      <p className="text-gray-900">{currentLead.contactPerson || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Email</label>
                      <p className="text-gray-900">{currentLead.email || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Phone</label>
                      <p className="text-gray-900">{currentLead.phoneNumber || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Industry</label>
                      <p className="text-gray-900">{currentLead.industry || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Company Size</label>
                      <p className="text-gray-900">{currentLead.companySize || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Location</label>
                      <p className="text-gray-900">{currentLead.location || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Priority</label>
                      <Badge className={currentLead.priority === 'HOT' ? 'bg-red-100 text-red-800' : 
                                      currentLead.priority === 'WARM' ? 'bg-orange-100 text-orange-800' : 
                                      'bg-blue-100 text-blue-800'}>
                        {currentLead.priority}
                      </Badge>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Expected Close</label>
                      <p className="text-gray-900">
                        {currentLead.expectedClose ? new Date(currentLead.expectedClose).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not set'}
                      </p>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Timeline Tab */}
              <TabsContent value="timeline" className="mt-6">
                <div className="space-y-4">
                  <div className="flex space-x-2">
                    <select
                      value={newActivity.type}
                      onChange={(e) => setNewActivity({ ...newActivity, type: e.target.value })}
                      className="px-3 py-2 border rounded-md w-32"
                    >
                      <option value="CALL">📞 Call</option>
                      <option value="EMAIL">📧 Email</option>
                      <option value="MEETING">🤝 Meeting</option>
                      <option value="NOTE">📝 Note</option>
                    </select>
                    <Input
                      placeholder="Add timeline event..."
                      value={newActivity.description}
                      onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                      className="flex-1"
                    />
                    <Button
                      onClick={() => addActivityMutation.mutate({ type: newActivity.type, content: newActivity.description })}
                      disabled={!newActivity.description.trim() || addActivityMutation.isPending}
                      size="sm"
                    >
                      {addActivityMutation.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {activitiesLoading ? (
                      <div className="flex items-center py-4">
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                        <span>Loading activities...</span>
                      </div>
                    ) : activities.length === 0 ? (
                      <p className="text-gray-500 py-4">No activities yet</p>
                    ) : (
                      activities.map((activity: any) => (
                        <div key={activity.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-sm">{activity.type}</span>
                              <span className="text-xs text-gray-500">
                                {new Date(activity.createdAt || activity.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 mt-1">
                              {activity.description || activity.content}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Tasks Tab */}
              <TabsContent value="tasks" className="mt-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="Task title"
                      value={newTask.title}
                      onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    />
                    <Input
                      type="date"
                      value={newTask.dueDate}
                      onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                    />
                    <div className="flex space-x-2">
                      <select
                        value={newTask.priority}
                        onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                        className="px-3 py-2 border rounded-md flex-1"
                      >
                        <option value="LOW">🟢 Low</option>
                        <option value="MEDIUM">🟡 Medium</option>
                        <option value="HIGH">🟠 High</option>
                        <option value="URGENT">🔴 Urgent</option>
                      </select>
                      <Button
                        onClick={() => addTaskMutation.mutate(newTask)}
                        disabled={!newTask.title.trim() || !newTask.dueDate || addTaskMutation.isPending}
                        size="sm"
                      >
                        {addTaskMutation.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {tasksLoading ? (
                      <div className="flex items-center py-4">
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                        <span>Loading tasks...</span>
                      </div>
                    ) : tasks.length === 0 ? (
                      <p className="text-gray-500 py-4">No tasks assigned</p>
                    ) : (
                      tasks.map((task: any) => (
                        <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">{task.title}</p>
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                              <span>{task.status}</span>
                              <span>•</span>
                              <span>{task.description}</span>
                              {task.dueDate && (
                                <>
                                  <span>•</span>
                                  <span>{new Date(task.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <Badge variant="outline">{task.priority}</Badge>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Notes Tab */}
              <TabsContent value="notes" className="mt-6">
                <div className="space-y-4">
                  <div className="flex space-x-2">
                    <Textarea
                      placeholder="Add a note..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="flex-1 min-h-[100px]"
                    />
                    <Button
                      onClick={() => addNoteMutation.mutate(newNote)}
                      disabled={!newNote.trim() || addNoteMutation.isPending}
                      size="sm"
                      className="self-end"
                    >
                      {addNoteMutation.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {notesLoading ? (
                      <div className="flex items-center py-4">
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                        <span>Loading notes...</span>
                      </div>
                    ) : notes.length === 0 ? (
                      <p className="text-gray-500 py-4">No notes added yet</p>
                    ) : (
                      notes.map((note: any) => (
                        <div key={note.id} className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm">{note.content}</p>
                          <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                            <span>{new Date(note.createdAt).toLocaleString()}</span>
                            <span>{note.author?.name || 'Anonymous'}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Files Tab */}
              <TabsContent value="files" className="mt-6">
                <div className="space-y-4">
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadFileMutation.isPending}
                      className="w-full"
                    >
                      {uploadFileMutation.isPending ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                      Upload File
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {filesLoading ? (
                      <div className="flex items-center py-4">
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                        <span>Loading files...</span>
                      </div>
                    ) : files.length === 0 ? (
                      <p className="text-gray-500 py-4">No files uploaded yet</p>
                    ) : (
                      files.map((file: any) => (
                        <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">{file.fileName}</p>
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                              <span>{(file.fileSize / 1024).toFixed(1)} KB</span>
                              <span>•</span>
                              <span>{new Date(file.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            </div>
                          </div>
                          <Button size="sm" variant="outline">
                            Download
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Edit Lead Modal
const EditLeadModal: React.FC<{
  lead: Lead | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}> = ({ lead, isOpen, onClose, onSave }) => {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const canAssign = ['CEO', 'GROUP_MANAGER', 'MANAGER'].includes(currentUser?.role || '');

  const [formData, setFormData] = useState({
    name: '', company: '', email: '', phoneNumber: '', contactPerson: '',
    industry: '', location: '', priority: 'WARM' as Lead['priority'],
    stage: 'NEW' as Lead['stage'], dealValue: 0, expectedClose: '',
    assignedToId: '' as string,
  });

  // Fetch users for the Assigned To dropdown (managers+ only)
  const { data: usersForAssign = [] } = useQuery({
    queryKey: ['users-for-assign'],
    queryFn: () => api.getUsers(),
    staleTime: 60000,
    enabled: isOpen && canAssign,
  });

  React.useEffect(() => {
    if (lead && isOpen) {
      setFormData({
        name: lead.name || '', company: lead.company || '', email: lead.email || '',
        phoneNumber: lead.phoneNumber || '', contactPerson: lead.contactPerson || '',
        industry: lead.industry || '', location: lead.location || '',
        priority: lead.priority || 'WARM', stage: lead.stage || 'NEW',
        dealValue: lead.dealValue || lead.value || 0,
        expectedClose: lead.expectedClose ? lead.expectedClose.split('T')[0] : '',
        assignedToId: (lead as any).assignedToId || (lead as any).assignedTo?.id || '',
      });
    }
  }, [lead, isOpen]);

  const updateLeadMutation = useMutation({
    mutationFn: (data: any) => api.updateLead(lead!.id, data),
    onSuccess: () => {
      toast({ title: '✅ Lead updated successfully' });
      onSave();
    },
    onError: (error: any) => {
      toast({ title: '❌ Failed to update lead', description: error.message, variant: 'destructive' });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.company.trim()) {
      toast({ title: '❌ Name and company are required', variant: 'destructive' });
      return;
    }
    const payload: any = { ...formData, dealValue: formData.dealValue || 0 };
    // Only include assignedToId if the user has permission and selected a value
    if (canAssign && formData.assignedToId) {
      payload.assignedToId = formData.assignedToId;
    } else {
      delete payload.assignedToId;
    }
    // Strip empty strings to undefined for optional fields
    if (!payload.expectedClose) delete payload.expectedClose;
    updateLeadMutation.mutate(payload);
  };

  if (!lead || !isOpen) return null;

  const activeUsers = usersForAssign.filter((u: any) => u.status === 'ACTIVE');

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Edit Lead</h2>
              <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lead Name *</label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                  <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} required />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value as Lead['priority'] })} className="w-full px-3 py-2 border rounded-md">
                    <option value="HOT">Hot</option>
                    <option value="WARM">Warm</option>
                    <option value="COLD">Cold</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                  <select value={formData.stage} onChange={(e) => setFormData({ ...formData, stage: e.target.value as Lead['stage'] })} className="w-full px-3 py-2 border rounded-md">
                    {STAGES.map(stage => (
                      <option key={stage} value={stage}>{getStageIcon(stage)} {stage}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deal Value</label>
                  <Input type="number" value={formData.dealValue} onChange={(e) => setFormData({ ...formData, dealValue: parseInt(e.target.value) || 0 })} min="0" />
                </div>
              </div>

              {/* Assigned To — visible only for CEO/GROUP_MANAGER/MANAGER */}
              {canAssign && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                  <select
                    value={formData.assignedToId}
                    onChange={(e) => setFormData({ ...formData, assignedToId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">-- Unassigned --</option>
                    {activeUsers.map((u: any) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={updateLeadMutation.isPending} className="bg-green-600 hover:bg-green-700">
                  {updateLeadMutation.isPending ? (
                    <><RefreshCw className="h-4 w-4 animate-spin mr-2" />Updating...</>
                  ) : (
                    'Update Lead'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// MAIN COMPONENT: Complete Pipeline Board with all fixes
export const PipelineBoard: React.FC<PipelineBoardProps> = ({
  leads, onLeadClick, onDeleteLead, onLeadMove, onAddLead
}) => {
  const { user } = useAuth();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [filters, setFilters] = useState({
    search: '', stage: '', assignedTo: '', priority: ''
  });
  const [isWonModalOpen, setIsWonModalOpen] = useState(false);
  const [wonLead, setWonLead] = useState<Lead | null>(null);
  const [pendingStageChange, setPendingStageChange] = useState<{
    leadId: string;
    newStage: string;
    previousStage: string;
  } | null>(null);

  // Deal value required intercept (PROPOSAL / NEGOTIATION with no deal value)
  const [isDealValueModalOpen, setIsDealValueModalOpen] = useState(false);
  const [dealValueInput, setDealValueInput] = useState('');
  const [pendingDealValueMove, setPendingDealValueMove] = useState<{
    leadId: string;
    newStage: string;
    previousStage: string;
  } | null>(null);

  // Phase 6: Stage gate state
  const [isGateBlockedModalOpen, setIsGateBlockedModalOpen] = useState(false);
  const [gateBlockedState, setGateBlockedState] = useState<{
    lead: Lead;
    newStage: string;
    previousStage: string;
    missing: GateMissingItem[];
  } | null>(null);
  const [pendingBypass, setPendingBypass] = useState<{
    bypassGate: boolean;
    bypassReason: string;
  } | null>(null);
  const [gateStatus, setGateStatus] = useState<BatchGateStatus>({});

  // Hold modal state
  const [isHoldModalOpen, setIsHoldModalOpen] = useState(false);
  const [holdTargetLead, setHoldTargetLead] = useState<Lead | null>(null);
  const [holdReason, setHoldReason] = useState('');
  const [holdDays, setHoldDays] = useState(7);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch stage statistics with debug logging
  const { data: stageStats = [] } = useQuery({
    queryKey: ['stage-stats'],
    queryFn: () => api.getStageStats(),
    refetchInterval: 30000
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    })
  );

  // FIXED: Stage change mutation with comprehensive validation and debugging
  const stageChangeMutation = useMutation({
    mutationFn: async ({ leadId, newStage, previousStage, bypassGate, bypassReason, dealValue }: {
      leadId: string;
      newStage: Lead['stage'];
      previousStage: Lead['stage'];
      bypassGate?: boolean;
      bypassReason?: string;
      dealValue?: number;
    }) => {
      console.log('🔄 DRAG DROP DEBUG:');
      console.log('  Lead ID:', leadId);
      console.log('  Previous Stage:', previousStage);
      console.log('  New Stage:', newStage);
      console.log('  Valid Stages:', STAGES);
      
      // Validate stage values
      const validStages = ['NEW', 'CONTACTED', 'DISCUSSION', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'];
      if (!validStages.includes(newStage)) {
        throw new Error(`Invalid stage: ${newStage}. Must be one of: ${validStages.join(', ')}`);
      }
      
      console.log('✅ Stage validation passed. Updating to:', newStage);
      const updateData: Record<string, unknown> = { stage: newStage };
      if (bypassGate) {
        updateData.bypassGate = true;
        updateData.bypassReason = bypassReason;
      }
      if (dealValue) {
        updateData.dealValue = dealValue;
      }
      const result = await api.updateLead(leadId, updateData as any);
      console.log('✅ Lead stage updated successfully:', result);
      
      return result;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['stage-stats'] });
      toast({
        title: "✅ Lead moved successfully",
        description: `Moved to ${variables.newStage} stage`,
      });
      console.log(`✅ Lead successfully moved to ${variables.newStage}`);

      // Warn if lead moved to PROPOSAL+ without an expectedClose date
      const proposalPlusStages = ['PROPOSAL', 'NEGOTIATION'];
      if (proposalPlusStages.includes(variables.newStage)) {
        const movedLead = leads.find(l => l.id === variables.leadId);
        if (movedLead && !movedLead.expectedClose) {
          setTimeout(() => {
            toast({
              title: "⚠️ Expected Close Date Missing",
              description: `"${movedLead.name}" has no expected close date. Please set one for accurate forecasting.`,
              variant: "destructive",
            });
          }, 1500);
        }
      }
    },
    onError: (error: any, variables) => {
      console.error('❌ Failed to move lead - Full Error Details:', {
        error: error.message || error,
        leadId: variables.leadId,
        newStage: variables.newStage,
        previousStage: variables.previousStage,
        stack: error.stack
      });
      
      toast({
        title: "❌ Failed to move lead",
        description: `Error: ${error.message || 'Unknown error occurred'}. Please try again.`,
        variant: "destructive",
      });
      
      // Revert optimistic update
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    }
  });

  // Phase 6: Gate check helper — imperative fetch (not useQuery, fires on user action)
  const PIPELINE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const checkStageGate = async (leadId: string, targetStage: string): Promise<GateCheckResult> => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${PIPELINE_API_URL}/stage-gates/check?leadId=${leadId}&targetStage=${targetStage}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) return { blocked: false, missing: [], satisfied: [] };
      const result = await response.json();
      return result.data as GateCheckResult;
    } catch {
      // If gate check fails (e.g., backend not yet deployed), fail open
      return { blocked: false, missing: [], satisfied: [] };
    }
  };

  // Phase 6: Batch gate status pre-fetch for lock icon indicators
  const fetchBatchGateStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const [negResponse, wonResponse] = await Promise.all([
        fetch(`${PIPELINE_API_URL}/stage-gates/batch-status?stage=NEGOTIATION`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${PIPELINE_API_URL}/stage-gates/batch-status?stage=WON`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      const [negData, wonData] = await Promise.all([negResponse.json(), wonResponse.json()]);
      setGateStatus({ ...(negData.data || {}), ...(wonData.data || {}) });
    } catch {
      // Non-critical — gate status display is best-effort
    }
  };

  useEffect(() => {
    fetchBatchGateStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads.length]); // Re-fetch when lead count changes (after stage moves)

  // Phase 6: CEO bypass handler
  const handleGateBypassAsCEO = (reason: string) => {
    if (!gateBlockedState) return;
    const { lead, newStage, previousStage } = gateBlockedState;

    // Store bypass state for stageChangeMutation
    setPendingBypass({ bypassGate: true, bypassReason: reason });

    // Close gate modal
    setIsGateBlockedModalOpen(false);
    setGateBlockedState(null);

    // Re-enter the WON flow (bypass approved)
    if (newStage === 'WON') {
      setWonLead(lead);
      setPendingStageChange({ leadId: lead.id, newStage, previousStage });
      setIsWonModalOpen(true);
    } else {
      // Non-WON stage bypass: proceed directly
      onLeadMove(lead.id, newStage as Lead['stage']);
      stageChangeMutation.mutate({
        leadId: lead.id,
        newStage: newStage as Lead['stage'],
        previousStage: previousStage as Lead['stage'],
        bypassGate: true,
        bypassReason: reason
      });
      setPendingBypass(null);
    }
  };

  // FIXED: Drag handlers with proper ID management
  const handleDragStart = (event: DragStartEvent) => {
    const leadId = event.active.id as string;
    setActiveId(leadId);
    console.log('🎯 Drag started for lead:', leadId);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) {
      console.log('❌ Drag ended outside valid drop zone');
      return;
    }

    const leadId = active.id as string;
    let newStage: Lead['stage'];
    
    console.log('🎯 DRAG END DEBUG:');
    console.log('  Active ID (Lead ID):', leadId);
    console.log('  Over ID:', over.id);
    console.log('  Over data:', over.data);
    
    // CRITICAL FIX: Check if dropping on stage or lead
    const validStages = ['NEW', 'CONTACTED', 'DISCUSSION', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'];
    if (validStages.includes(over.id as string)) {
      // Dropped on stage column
      newStage = over.id as Lead['stage'];
      console.log('✅ Dropped on stage column:', newStage);
    } else {
      // Dropped on another lead - find which stage that lead belongs to
      const targetLead = leads.find(l => l.id === over.id);
      if (targetLead) {
        newStage = targetLead.stage;
        console.log('✅ Dropped on lead, using target lead stage:', newStage);
      } else {
        console.log('❌ Invalid drop target');
        return;
      }
    }
    
    const lead = leads.find(l => l.id === leadId);
    if (!lead || lead.stage === newStage) {
      console.log('❌ Lead not found or same stage');
      return;
    }

    const previousStage = lead.stage;

    // Warn on stage-skip (skipping more than 1 stage forward)
    const stageOrder = ['NEW', 'CONTACTED', 'DISCUSSION', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'];
    const fromIndex = stageOrder.indexOf(previousStage);
    const toIndex = stageOrder.indexOf(newStage);
    const isSkipping = newStage !== 'LOST' && toIndex - fromIndex > 1;

    if (isSkipping) {
      const skippedStages = stageOrder.slice(fromIndex + 1, toIndex).join(' → ');
      const confirmed = window.confirm(
        `You are skipping stages (${skippedStages}). Move "${lead.name}" directly from ${previousStage} to ${newStage}?`
      );
      if (!confirmed) return;
    }

    // Confirm backward stage moves (regression)
    if (toIndex < fromIndex && newStage !== 'LOST') {
      const confirmed = window.confirm(
        `Move "${lead.name}" backward from ${previousStage} to ${newStage}? This will regress the lead's progress.`
      );
      if (!confirmed) return;
    }

    // Phase 6: Gate check for NEGOTIATION and WON stages
    const gatedStages = ['NEGOTIATION', 'WON'];
    if (gatedStages.includes(newStage)) {
      const gateResult = await checkStageGate(leadId, newStage);
      if (gateResult.blocked) {
        setGateBlockedState({ lead, newStage, previousStage, missing: gateResult.missing });
        setIsGateBlockedModalOpen(true);
        return; // Stop — show blocking modal
      }
    }

    // Open subscription modal for WON stage
    if (newStage === 'WON') {
      setWonLead(lead);
      setPendingStageChange({ leadId, newStage, previousStage });
      setIsWonModalOpen(true);
      return;
    }

    if (newStage === 'LOST') {
      const confirmed = window.confirm(
        `Are you sure you want to mark "${lead.name}" as LOST? This indicates the deal has been lost.`
      );
      if (!confirmed) return;
    }

    // Intercept PROPOSAL/NEGOTIATION moves when lead has no deal value
    if (['PROPOSAL', 'NEGOTIATION'].includes(newStage) && !(lead.dealValue || (lead as any).value)) {
      setPendingDealValueMove({ leadId, newStage, previousStage });
      setDealValueInput('');
      setIsDealValueModalOpen(true);
      return;
    }

    // Optimistically update UI
    onLeadMove(leadId, newStage);

    // Update backend
    stageChangeMutation.mutate({ leadId, newStage, previousStage });
  };

  const handleWonModalConfirm = async (planId: string, startDate: string) => {
    if (!pendingStageChange || !wonLead) return;
    const { leadId, newStage, previousStage } = pendingStageChange;

    // 1. Optimistically update UI
    onLeadMove(leadId, newStage as Lead['stage']);

    // 2. Stage change (with bypass if CEO override was used)
    stageChangeMutation.mutate({
      leadId,
      newStage: newStage as Lead['stage'],
      previousStage: previousStage as Lead['stage'],
      ...(pendingBypass || {})
    });

    // 3. Create subscription. The backend auto-creates the plan's deliverables
    // as part of POST /subscriptions — do NOT also call
    // /stage-gates/auto-create-deliverables here or they will be duplicated (GATE-04).
    try {
      const token = localStorage.getItem('token');
      await fetch(`${PIPELINE_API_URL}/subscriptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ leadId, planId, startDate }),
      });
    } catch (err) {
      console.error('Failed to create subscription:', err);
    }

    setIsWonModalOpen(false);
    setWonLead(null);
    setPendingStageChange(null);
    setPendingBypass(null);
    // Refresh gate status after WON
    fetchBatchGateStatus();
  };

  const handleWonModalSkip = () => {
    if (!pendingStageChange) return;
    const { leadId, newStage, previousStage } = pendingStageChange;
    onLeadMove(leadId, newStage as Lead['stage']);
    stageChangeMutation.mutate({ leadId, newStage: newStage as Lead['stage'], previousStage: previousStage as Lead['stage'] });
    setIsWonModalOpen(false);
    setWonLead(null);
    setPendingStageChange(null);
  };

  const handleDealValueConfirm = () => {
    if (!pendingDealValueMove) return;
    const value = parseInt(dealValueInput, 10);
    if (value === null || value === undefined || isNaN(value) || value < 0) {
      toast({ title: 'Deal value required', description: 'Please enter a valid amount (0 or more)', variant: 'destructive' });
      return;
    }
    const { leadId, newStage, previousStage } = pendingDealValueMove;
    setIsDealValueModalOpen(false);
    setPendingDealValueMove(null);
    onLeadMove(leadId, newStage as Lead['stage']);
    stageChangeMutation.mutate({ leadId, newStage: newStage as Lead['stage'], previousStage: previousStage as Lead['stage'], dealValue: value } as any);
  };

  const handleLeadClickInternal = (lead: Lead) => {
    console.log('👆 Lead clicked:', lead.name);
    // Use the external onLeadClick handler to open LeadDetailPanel with Payments tab
    onLeadClick(lead);
  };

  const handleEditLead = (lead: Lead) => {
    console.log('Edit lead requested for:', lead.name);
    setEditingLead(lead);
    setIsEditModalOpen(true);
  };

  // Hold / Resume mutations
  const holdMutation = useMutation({
    mutationFn: async ({ leadId, holdReason, durationDays }: { leadId: string; holdReason: string; durationDays: number }) => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${PIPELINE_API_URL}/leads/${leadId}/hold`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ holdReason, durationDays }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to place lead on hold');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['stage-stats'] });
      toast({ title: 'Lead placed on hold', description: `Will auto-resume in ${holdDays} day(s)` });
      setIsHoldModalOpen(false);
      setHoldTargetLead(null);
      setHoldReason('');
      setHoldDays(7);
    },
    onError: (error: any) => {
      toast({ title: 'Failed to place lead on hold', description: error.message, variant: 'destructive' });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: async (leadId: string) => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${PIPELINE_API_URL}/leads/${leadId}/resume`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to resume lead');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['stage-stats'] });
      toast({ title: 'Lead resumed from hold' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to resume lead', description: error.message, variant: 'destructive' });
    },
  });

  const handleHoldLead = (lead: Lead) => {
    setHoldTargetLead(lead);
    setHoldReason('');
    setHoldDays(7);
    setIsHoldModalOpen(true);
  };

  const handleResumeLead = (lead: Lead) => {
    resumeMutation.mutate(lead.id);
  };

  const handleHoldConfirm = () => {
    if (!holdTargetLead) return;
    if (!holdReason.trim()) {
      toast({ title: 'Hold reason required', description: 'Please enter a reason for placing this lead on hold', variant: 'destructive' });
      return;
    }
    if (holdDays < 1 || holdDays > 90) {
      toast({ title: 'Invalid duration', description: 'Duration must be between 1 and 90 days', variant: 'destructive' });
      return;
    }
    holdMutation.mutate({ leadId: holdTargetLead.id, holdReason: holdReason.trim(), durationDays: holdDays });
  };

  // Filter leads
  const getLeadsForStage = (stage: Lead['stage']) => {
    return leads.filter(lead => {
      const matchesStage = lead.stage === stage;
      const matchesSearch = !filters.search ||
        lead.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        lead.company.toLowerCase().includes(filters.search.toLowerCase());
      const matchesPriority = !filters.priority || filters.priority === 'ALL' || lead.priority === filters.priority;
      const matchesAssignedTo = !filters.assignedTo || lead.assignedToId === filters.assignedTo;
      
      return matchesStage && matchesSearch && matchesPriority && matchesAssignedTo;
    });
  };

  const getStageStats = (stage: Lead['stage']) => {
    const stats = stageStats.find(stat => stat.stage === stage);
    console.log(`📊 Stage ${stage} stats:`, stats);
    return stats;
  };

  if (!leads) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin mr-2" />
        <span>Loading pipeline...</span>
      </div>
    );
  }

  // Check if user has any leads at all
  const hasAnyLeads = leads.length > 0;

  return (
    <div className="h-full">
      {/* Empty State for users with NO leads */}
      {!hasAnyLeads && (
        <div className="flex flex-col items-center justify-center h-96 bg-white rounded-lg shadow-sm p-8">
          <Building className="h-16 w-16 text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No leads assigned to you yet</h3>
          <p className="text-gray-500 text-center mb-6 max-w-md">
            You currently have no leads in your pipeline. Once leads are assigned to you, they will appear here organized by stage.
          </p>
          <Button onClick={onAddLead} size="lg" className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-5 w-5 mr-2" />
            Create Your First Lead
          </Button>
        </div>
      )}

      {/* Normal Pipeline View */}
      {hasAnyLeads && (
        <>
          {/* Filters */}
          <div className="mb-6 bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search leads by name or company..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
              />
            </div>
          </div>
          {/* Fixed the Select component usage to avoid React Hook errors */}
          <div>
            <Select value={filters.stage} onValueChange={(value: string) => setFilters({ ...filters, stage: value })}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Stage" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Stages</SelectItem>
                {STAGES.map(stage => (
                  <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Select value={filters.priority} onValueChange={(value: string) => setFilters({ ...filters, priority: value })}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Priorities</SelectItem>
                <SelectItem value="HOT">Hot</SelectItem>
                <SelectItem value="WARM">Warm</SelectItem>
                <SelectItem value="COLD">Cold</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => setFilters({ search: '', stage: '', assignedTo: '', priority: '' })}>
            <X className="h-4 w-4 mr-2" />
            Clear
          </Button>
        </div>
      </div>

      {/* Stage update indicator */}
      {stageChangeMutation.isPending && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg mb-4 text-sm text-blue-700">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Updating lead stage...
        </div>
      )}

      {/* FIXED: Drag and Drop Context with proper collision detection */}
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4 h-full">
          {STAGES.map((stage) => (
            <DroppableStageColumn
              key={stage}
              stage={stage}
              leads={getLeadsForStage(stage)}
              stageStats={getStageStats(stage)}
              onLeadClick={handleLeadClickInternal}
              onDeleteLead={onDeleteLead}
              onEditLead={handleEditLead}
              onHoldLead={handleHoldLead}
              onResumeLead={handleResumeLead}
              onAddLead={onAddLead}
              gateStatus={gateStatus}
            />
          ))}
        </div>

        <DragOverlay>
          {activeId ? (() => {
            const draggedLead = leads.find(l => l.id === activeId);
            return (
              <Card className="p-4 shadow-xl border-2 border-blue-500 bg-white rotate-3" aria-label={`Dragging ${draggedLead?.name || 'lead'}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900 truncate">{draggedLead?.name || 'Moving lead...'}</h4>
                  <TrendingUp className="h-4 w-4 text-blue-500" />
                </div>
                {draggedLead?.company && <p className="text-xs text-gray-500 truncate">{draggedLead.company}</p>}
              </Card>
            );
          })() : null}
        </DragOverlay>
      </DndContext>

      {/* Modals */}
      <LeadDetailsModal
        lead={selectedLead}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedLead(null);
        }}
      />

      <EditLeadModal
        lead={editingLead}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingLead(null);
        }}
        onSave={() => {
          setIsEditModalOpen(false);
          setEditingLead(null);
          queryClient.invalidateQueries({ queryKey: ['leads'] });
          queryClient.invalidateQueries({ queryKey: ['stage-stats'] });
        }}
      />

      <WonSubscriptionModal
        isOpen={isWonModalOpen}
        lead={wonLead}
        onConfirm={handleWonModalConfirm}
        onSkip={handleWonModalSkip}
        onClose={() => {
          setIsWonModalOpen(false);
          setWonLead(null);
          setPendingStageChange(null);
        }}
      />

      <StageBlockedModal
        isOpen={isGateBlockedModalOpen}
        onClose={() => {
          setIsGateBlockedModalOpen(false);
          setGateBlockedState(null);
        }}
        onBypassAsCEO={user?.role === 'CEO' ? handleGateBypassAsCEO : undefined}
        targetStage={gateBlockedState?.newStage || ''}
        leadName={gateBlockedState?.lead?.name || ''}
        missing={gateBlockedState?.missing || []}
        isCEO={user?.role === 'CEO'}
      />

      {/* Hold Lead Modal */}
      {isHoldModalOpen && holdTargetLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <h2 className="text-lg font-bold text-gray-900 mb-1">Place Lead on Hold</h2>
            <p className="text-sm text-gray-500 mb-4">
              Pausing <span className="font-semibold text-amber-600">{holdTargetLead.name}</span>. The lead will auto-resume after the hold period.
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
            <textarea
              autoFocus
              rows={3}
              value={holdReason}
              onChange={e => setHoldReason(e.target.value)}
              placeholder="e.g. Brand requested pause for festival season"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 mb-3 resize-none"
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">Duration (days) *</label>
            <input
              type="number"
              min={1}
              max={90}
              value={holdDays}
              onChange={e => setHoldDays(Math.max(1, Math.min(90, parseInt(e.target.value) || 1)))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 mb-2"
            />
            <p className="text-xs text-gray-400 mb-4">
              Auto-resumes on: <span className="font-medium text-gray-600">{new Date(Date.now() + holdDays * 86400000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </p>

            <div className="flex gap-2">
              <button
                onClick={handleHoldConfirm}
                disabled={holdMutation.isPending}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {holdMutation.isPending ? 'Placing on Hold...' : 'Place on Hold'}
              </button>
              <button
                onClick={() => {
                  setIsHoldModalOpen(false);
                  setHoldTargetLead(null);
                  setHoldReason('');
                  setHoldDays(7);
                }}
                className="flex-1 border border-gray-300 text-gray-700 text-sm font-medium py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deal Value Required Modal — shown when dragging to PROPOSAL/NEGOTIATION without a deal value */}
      {isDealValueModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <h2 className="text-lg font-bold text-gray-900 mb-1">Deal Value Required</h2>
            <p className="text-sm text-gray-500 mb-4">
              Moving to <span className="font-semibold text-orange-600">{pendingDealValueMove?.newStage}</span> stage requires a deal value.
              Enter the expected deal amount to continue.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal Value (₹) *</label>
            <input
              type="number"
              min="0"
              autoFocus
              value={dealValueInput}
              onChange={e => setDealValueInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleDealValueConfirm()}
              placeholder="e.g. 50000"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-1"
            />
            <p className="text-xs text-gray-500 mt-1 mb-4">Enter 0 for leads with no commercial value discussed</p>
            <div className="flex gap-2">
              <button
                onClick={handleDealValueConfirm}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-lg transition-colors"
              >
                Confirm Move
              </button>
              <button
                onClick={() => {
                  setIsDealValueModalOpen(false);
                  setPendingDealValueMove(null);
                  setDealValueInput('');
                }}
                className="flex-1 border border-gray-300 text-gray-700 text-sm font-medium py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
    )}
    </div>
  );
}; 